// lib/drift-harmonization.ts
import type { CV, Mat } from '@techstark/opencv-js';
import { computeChangeMask } from '#lib/auto-cut';
import { performColorHarmonization } from '#lib/color-harmonization';
import { reprojectStudioLayerWorldImageToLocal, type StudioLayerTransform } from '#lib/studio-layer-transform';
import type { Rect } from '#types/types';

/*
 * Colour drift of a generated layer against what lies beneath it, measured where the generation left the content
 * unchanged and solved across what it changed. The drift is a low-frequency (L, a, b) field, estimated and solved
 * at analysis resolution and applied to the layer at its own in CIELab (D65, float), rounded once to 8-bit sRGB:
 * in full on the unchanged area, fading to nothing inside what changed so new content keeps its own colours.
 * Ranked by scripts/qa/border-color-benchmark.mjs.
 */

// sRGB <-> CIELab D65. Encoding counts the decoded midpoints below a value, which is the exact 8-bit rounding in
// the encoded domain, so an unchanged Lab value always returns its original code.
const decode = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const LINEAR = Float64Array.from({ length: 256 }, (_, v) => decode(v / 255));
const MIDPOINTS = Float64Array.from({ length: 255 }, (_, k) => decode((k + 0.5) / 255));
const CODE_BINS = 4096;
/** Code of the lower edge of each linear bin; a bin never spans more than a couple of codes, walked up below. */
const CODE_AT_BIN = Uint8Array.from({ length: CODE_BINS }, (_, bin) => {
  const code = MIDPOINTS.findIndex((midpoint) => midpoint >= bin / CODE_BINS);
  return code === -1 ? 255 : code;
});
const encode = (linear: number) => {
  let code = linear <= 0 ? 0 : linear >= 1 ? 255 : CODE_AT_BIN[Math.floor(linear * CODE_BINS)]!;
  while (code < 255 && MIDPOINTS[code]! < linear) code += 1;
  return code;
};
const M = [0.4124564, 0.3575761, 0.1804375, 0.2126729, 0.7151522, 0.072175, 0.0193339, 0.119192, 0.9503041];
const INV = (() => {
  const [a, b, c, d, e, f, g, h, i] = M as [number, number, number, number, number, number, number, number, number];
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  return [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det, (f * g - d * i) / det, (a * i - c * g) / det,
    (c * d - a * f) / det, (d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det];
})();
const WHITE = [M[0]! + M[1]! + M[2]!, M[3]! + M[4]! + M[5]!, M[6]! + M[7]! + M[8]!];
const EPSILON = 216 / 24389;
const KAPPA = 24389 / 27;
const f = (t: number) => (t > EPSILON ? Math.cbrt(t) : (KAPPA * t + 16) / 116);
const fInverse = (t: number) => (t * t * t > EPSILON ? t * t * t : (116 * t - 16) / KAPPA);

export const pixelLab = (data: Uint8ClampedArray, index: number, out: Float64Array | Float32Array, at = 0) => {
  const r = LINEAR[data[index]!]!;
  const g = LINEAR[data[index + 1]!]!;
  const b = LINEAR[data[index + 2]!]!;
  const fx = f((M[0]! * r + M[1]! * g + M[2]! * b) / WHITE[0]!);
  const fy = f((M[3]! * r + M[4]! * g + M[5]! * b) / WHITE[1]!);
  const fz = f((M[6]! * r + M[7]! * g + M[8]! * b) / WHITE[2]!);
  out[at] = 116 * fy - 16;
  out[at + 1] = 500 * (fx - fy);
  out[at + 2] = 200 * (fy - fz);
};

/** Lab -> 8-bit sRGB at `out[index..index + 2]`. */
export const labRgb = (l: number, a: number, b: number, out: Uint8ClampedArray, index: number) => {
  const fy = (l + 16) / 116;
  const x = fInverse(fy + a / 500) * WHITE[0]!;
  const y = fInverse(fy) * WHITE[1]!;
  const z = fInverse(fy - b / 200) * WHITE[2]!;
  out[index] = encode(INV[0]! * x + INV[1]! * y + INV[2]! * z);
  out[index + 1] = encode(INV[3]! * x + INV[4]! * y + INV[5]! * z);
  out[index + 2] = encode(INV[6]! * x + INV[7]! * y + INV[8]! * z);
};

export const toLab = (image: ImageData) => {
  const lab = new Float32Array(image.width * image.height * 3);
  for (let i = 0, n = image.width * image.height; i < n; i += 1) pixelLab(image.data, i * 4, lab, i * 3);
  return lab;
};

export const FIELD_LONG_SIDE = 256;
export const CASCADE_COARSEST = 32;
export const SOLVER_TOLERANCE = 1e-5;

/** The cell-centred field grid of a `width` x `height` image with `longSide` nodes on its long side. */
export const fieldGrid = (width: number, height: number, longSide: number) => {
  const w = Math.max(2, Math.round(width * longSide / Math.max(width, height)));
  const h = Math.max(2, Math.round(height * longSide / Math.max(width, height)));
  return { w, h, cellX: width / w, cellY: height / h, cell: Math.max(width / w, height / h) };
};

/**
 * Preconditioned conjugate gradient on (Laplacian - k^2) u = 0 over the free nodes, u fixed on the Dirichlet nodes,
 * started from `initial`. A free node on the grid edge simply has fewer neighbours: a natural (Neumann) boundary.
 * Stops when the residual falls under SOLVER_TOLERANCE times the right-hand side.
 */
export const solveScreened = (fixed: Uint8Array, values: Float64Array, width: number, height: number, k2: number, initial: Float64Array) => {
  const n = width * height;
  const diagonal = new Float64Array(n);
  const b = new Float64Array(n);
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const i = y * width + x;
    if (fixed[i]) continue;
    let count = 0;
    for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      count += 1;
      if (fixed[ny * width + nx]) b[i]! += values[ny * width + nx]!;
    }
    diagonal[i] = count + k2;
  }
  const apply = (p: Float64Array, out: Float64Array) => {
    for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
      const i = y * width + x;
      if (fixed[i]) { out[i] = 0; continue; }
      let sum = diagonal[i]! * p[i]!;
      if (x > 0 && !fixed[i - 1]) sum -= p[i - 1]!;
      if (x < width - 1 && !fixed[i + 1]) sum -= p[i + 1]!;
      if (y > 0 && !fixed[i - width]) sum -= p[i - width]!;
      if (y < height - 1 && !fixed[i + width]) sum -= p[i + width]!;
      out[i] = sum;
    }
  };
  const u = Float64Array.from(initial, (value, i) => (fixed[i] ? 0 : value));
  const z = new Float64Array(n);
  const p = new Float64Array(n);
  const ap = new Float64Array(n);
  apply(u, ap);
  const r = Float64Array.from(b, (value, i) => value - ap[i]!);
  const bNorm = Math.sqrt(b.reduce((sum, value) => sum + value * value, 0));
  let iterations = 0;
  let relative = bNorm > 0 ? Math.sqrt(r.reduce((sum, value) => sum + value * value, 0)) / bNorm : 0;
  if (relative >= SOLVER_TOLERANCE) {
    let rz = 0;
    for (let i = 0; i < n; i += 1) if (!fixed[i]) { z[i] = r[i]! / diagonal[i]!; p[i] = z[i]!; rz += r[i]! * z[i]!; }
    for (; iterations < 20_000; iterations += 1) {
      apply(p, ap);
      let pap = 0;
      for (let i = 0; i < n; i += 1) pap += p[i]! * ap[i]!;
      const alpha = rz / pap;
      let rr = 0;
      for (let i = 0; i < n; i += 1) { u[i]! += alpha * p[i]!; r[i]! -= alpha * ap[i]!; rr += r[i]! * r[i]!; }
      relative = Math.sqrt(rr) / bNorm;
      if (relative < SOLVER_TOLERANCE) { iterations += 1; break; }
      let next = 0;
      for (let i = 0; i < n; i += 1) if (!fixed[i]) { z[i] = r[i]! / diagonal[i]!; next += r[i]! * z[i]!; }
      const beta = next / rz;
      rz = next;
      for (let i = 0; i < n; i += 1) p[i] = z[i]! + beta * p[i]!;
    }
  }
  for (let i = 0; i < n; i += 1) if (fixed[i]) u[i] = values[i]!;
  return { u, iterations, relative };
};

/** Bilinear value of a cell-centred grid at pixel (x, y), clamped at the edges. */
export const sampleGrid = (grid: Float64Array, w: number, h: number, cellX: number, cellY: number, x: number, y: number) => {
  const gx = Math.min(w - 1, Math.max(0, (x + 0.5) / cellX - 0.5));
  const gy = Math.min(h - 1, Math.max(0, (y + 0.5) / cellY - 0.5));
  const x0 = Math.floor(gx);
  const y0 = Math.floor(gy);
  const x1 = Math.min(w - 1, x0 + 1);
  const y1 = Math.min(h - 1, y0 + 1);
  const wx = gx - x0;
  const wy = gy - y0;
  return (1 - wy) * ((1 - wx) * grid[y0 * w + x0]! + wx * grid[y0 * w + x1]!) + wy * ((1 - wx) * grid[y1 * w + x0]! + wx * grid[y1 * w + x1]!);
};

/** Alpha at or above this is opaque, on both the layer and the underlying render. */
export const OPAQUE_ALPHA = 250;
/** Harmonize's own scale: the sigma OpenCV derives for its 81 px blur kernel. */
export const HARMONIZE_SIGMA_PX = 0.3 * ((81 - 1) * 0.5 - 1) + 0.8;
/** Below this confidence (Gaussian-weighted fraction of unchanged pixels around a point) the drift is not measured there. */
export const DRIFT_CONFIDENCE_MIN = 0.2;
/** Unchanged pixels below this fraction of the opaque layer are too few to measure a drift. */
export const MIN_UNCHANGED_FRACTION = 0.05;
/**
 * Plausible drift: underlying minus layer, blurred at PLAUSIBLE_DRIFT_SIGMA_PX, further than this dE76 from its median
 * over the unchanged pixels is a change, and so is PLAUSIBLE_DRIFT_MARGIN_PX around it; analysis px. Chosen on five real cases.
 */
export const PLAUSIBLE_DRIFT_DE = 12;
export const PLAUSIBLE_DRIFT_SIGMA_PX = 4;
export const PLAUSIBLE_DRIFT_MARGIN_PX = 8;

export type DriftMethod = {
  /** Normalized-convolution scale, analysis px. */
  sigma: number;
  /** How the field crosses what changed: harmonic (membrane) or decaying to zero over lFraction of the short side (screened). */
  fill: 'membrane' | 'screened';
  lFraction?: number;
};

/** The configuration the Studio ships, chosen on the benchmark. */
export const DRIFT_HARMONIZATION_METHOD: DriftMethod = { sigma: HARMONIZE_SIGMA_PX, fill: 'membrane' };
/** Fraction of the analysis short side over which the correction fades to zero inside what changed, chosen on the four real cases. */
export const DRIFT_INTERIOR_FALLOFF = 0.023;

export type DriftField = {
  /** Per Lab channel, the correction on the FIELD_LONG_SIDE grid of the analysis image. */
  grids: Float64Array[];
  gridWidth: number;
  gridHeight: number;
  /** Analysis size. */
  width: number;
  height: number;
  /** 0 = unchanged, 255 = changed, analysis pixels. */
  changeMask: Uint8Array;
  /** Share of the field applied, analysis pixels: 1 where unchanged, smoothstep down to 0 inside what changed. */
  weights: Float32Array;
  unchangedFraction: number;
  iterations: number;
  relativeResidual: number;
};

/**
 * The drift field of `layer` over `underlying` (same size, layer space). Change is detected after a soft harmonize
 * so a drift alone does not read as change, plus wherever the drift is not plausible; the drift itself is read on
 * the original layer. Null when too little of the opaque layer lies unchanged over opaque content.
 */
export const measureDrift = async (cv: CV, underlying: ImageData, layer: ImageData, method: DriftMethod): Promise<DriftField | null> => {
  const { width, height } = layer;
  const harmonized = await performColorHarmonization(underlying, layer, cv, 'soft');
  if (!harmonized) throw new Error('The color drift of this layer could not be measured.');
  const owned: { delete(): void }[] = [];
  const own = <T extends { delete(): void }>(mat: T) => { owned.push(mat); return mat; };
  try {
    const mask = own(computeChangeMask(cv, own(cv.matFromImageData(underlying)), own(cv.matFromImageData(harmonized))) as Mat);
    const difference = own(new cv.Mat(height, width, cv.CV_32FC3));
    {
      const out = difference.data32F;
      const lab = new Float64Array(6);
      for (let i = 0; i < width * height; i += 1) {
        pixelLab(underlying.data, i * 4, lab);
        pixelLab(layer.data, i * 4, lab, 3);
        for (let c = 0; c < 3; c += 1) out[i * 3 + c] = lab[c]! - lab[c + 3]!;
      }
    }
    // The soft pre-pass transfers colour at its 81 px scale, so it can repaint a changed object of about that size
    // in the colours beneath it: the mask then reads the object as unchanged and its whole change as a drift. A
    // drift is smooth, so what departs from the typical one is a change. The median is subtracted because a real
    // global drift above PLAUSIBLE_DRIFT_DE would otherwise flag everything and refuse the correction.
    const smooth = own(new cv.Mat());
    cv.GaussianBlur(difference, smooth, new cv.Size(0, 0), PLAUSIBLE_DRIFT_SIGMA_PX, PLAUSIBLE_DRIFT_SIGMA_PX, cv.BORDER_REFLECT_101);
    const implausible = own(cv.Mat.zeros(height, width, cv.CV_8U));
    {
      // Views are taken after the last allocation: a WASM heap growth detaches the earlier ones.
      const smoothed = smooth.data32F;
      const unchangedNow = mask.data;
      const flags = implausible.data;
      const stride = 7;
      const samples = [0, 1, 2].map(() => new Float32Array(Math.ceil(width * height / stride)));
      let count = 0;
      for (let i = 0; i < width * height; i += stride) {
        if (unchangedNow[i]! >= 128 || layer.data[i * 4 + 3]! < OPAQUE_ALPHA || underlying.data[i * 4 + 3]! < OPAQUE_ALPHA) continue;
        for (let c = 0; c < 3; c += 1) samples[c]![count] = smoothed[i * 3 + c]!;
        count += 1;
      }
      const [ml, ma, mb] = samples.map((values) => (count > 0 ? values.subarray(0, count).sort()[count >> 1]! : 0));
      for (let i = 0; i < width * height; i += 1) {
        const dl = smoothed[i * 3]! - ml!;
        const da = smoothed[i * 3 + 1]! - ma!;
        const db = smoothed[i * 3 + 2]! - mb!;
        if (dl * dl + da * da + db * db > PLAUSIBLE_DRIFT_DE * PLAUSIBLE_DRIFT_DE) flags[i] = 255;
      }
    }
    const margin = 2 * PLAUSIBLE_DRIFT_MARGIN_PX + 1;
    cv.dilate(implausible, implausible, own(cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(margin, margin))));
    cv.max(mask, implausible, mask);
    const changeMask = new Uint8Array(mask.data);
    const weighted = own(cv.Mat.zeros(height, width, cv.CV_32FC4));
    const sums = weighted.data32F;
    const drift = difference.data32F;
    let opaque = 0;
    let unchanged = 0;
    for (let i = 0; i < width * height; i += 1) {
      if (layer.data[i * 4 + 3]! < OPAQUE_ALPHA) continue;
      opaque += 1;
      if (underlying.data[i * 4 + 3]! < OPAQUE_ALPHA) continue;
      const weight = 1 - changeMask[i]! / 255;
      if (weight === 0) continue;
      unchanged += weight;
      for (let c = 0; c < 3; c += 1) sums[i * 4 + c] = weight * drift[i * 3 + c]!;
      sums[i * 4 + 3] = weight;
    }
    if (opaque === 0 || unchanged < MIN_UNCHANGED_FRACTION * opaque) return null;
    const blurred = own(new cv.Mat());
    cv.GaussianBlur(weighted, blurred, new cv.Size(0, 0), method.sigma, method.sigma, cv.BORDER_REFLECT_101);

    const lPx = (method.lFraction ?? 0) * Math.min(width, height);
    let iterations = 0;
    let relativeResidual = 0;
    let previous: { grids: Float64Array[]; w: number; h: number; cellX: number; cellY: number } | null = null;
    // Cascadic solve: each level starts from the bilinear upsampling of the coarser one, so CG is left with the
    // high-frequency error only.
    for (let longSide = CASCADE_COARSEST; longSide <= FIELD_LONG_SIDE; longSide *= 2) {
      const level = fieldGrid(width, height, longSide);
      const resized = own(new cv.Mat());
      cv.resize(blurred, resized, new cv.Size(level.w, level.h), 0, 0, cv.INTER_AREA);
      const cells = resized.data32F;
      const fixed = new Uint8Array(level.w * level.h);
      const values = [0, 1, 2].map(() => new Float64Array(level.w * level.h));
      for (let i = 0; i < level.w * level.h; i += 1) {
        const confidence = cells[i * 4 + 3]!;
        if (confidence < DRIFT_CONFIDENCE_MIN) continue;
        fixed[i] = 1;
        for (let c = 0; c < 3; c += 1) values[c]![i] = cells[i * 4 + c]! / confidence;
      }
      if (longSide === FIELD_LONG_SIDE && !fixed.includes(1)) return null;
      const k2 = method.fill === 'screened' ? (level.cell / lPx) ** 2 : 0;
      const grids = values.map((fixedValues, c) => {
        const initial = new Float64Array(level.w * level.h);
        if (previous) {
          for (let j = 0; j < level.h; j += 1) for (let i = 0; i < level.w; i += 1) {
            initial[j * level.w + i] = sampleGrid(previous.grids[c]!, previous.w, previous.h, previous.cellX, previous.cellY, (i + 0.5) * level.cellX - 0.5, (j + 0.5) * level.cellY - 0.5);
          }
        }
        const solved = solveScreened(fixed, fixedValues, level.w, level.h, k2, initial);
        if (longSide === FIELD_LONG_SIDE) {
          iterations = Math.max(iterations, solved.iterations);
          relativeResidual = Math.max(relativeResidual, solved.relative);
        }
        return solved.u;
      });
      previous = { grids, w: level.w, h: level.h, cellX: level.cellX, cellY: level.cellY };
    }
    const changed = own(new cv.Mat());
    cv.threshold(mask, changed, 127, 255, cv.THRESH_BINARY);
    const distance = own(new cv.Mat());
    cv.distanceTransform(changed, distance, cv.DIST_L2, 5);
    const falloff = DRIFT_INTERIOR_FALLOFF * Math.min(width, height);
    const weights = Float32Array.from(distance.data32F, (d) => {
      const t = Math.min(1, d / falloff);
      return 1 - t * t * (3 - 2 * t);
    });
    return {
      grids: previous!.grids, gridWidth: previous!.w, gridHeight: previous!.h, width, height, changeMask, weights,
      unchangedFraction: unchanged / opaque, iterations, relativeResidual,
    };
  } finally {
    owned.forEach((mat) => mat.delete());
  }
};

/** `target` (the layer at any resolution) plus the field times its weight, both bilinear; alpha untouched. */
export const applyDriftField = (target: ImageData, field: DriftField) => {
  const { gridWidth: w, gridHeight: h, grids, weights, width: analysisWidth } = field;
  const axis = (size: number, nodes: number) => {
    const cell = size / nodes;
    const low = new Uint32Array(size);
    const high = new Uint32Array(size);
    const weight = new Float64Array(size);
    for (let p = 0; p < size; p += 1) {
      const g = Math.min(nodes - 1, Math.max(0, (p + 0.5) / cell - 0.5));
      low[p] = Math.floor(g);
      high[p] = Math.min(nodes - 1, low[p]! + 1);
      weight[p] = g - low[p]!;
    }
    return { low, high, weight };
  };
  const columns = axis(target.width, w);
  const rows = axis(target.height, h);
  const pixelColumns = axis(target.width, analysisWidth);
  const pixelRows = axis(target.height, field.height);
  const source = target.data;
  const rgba = new Uint8ClampedArray(source.length);
  // One pixel at a time through a Lab triplet: a full-resolution Lab copy of an upscaled layer does not fit.
  const lab = new Float64Array(3);
  for (let y = 0; y < target.height; y += 1) {
    const top = rows.low[y]! * w;
    const bottom = rows.high[y]! * w;
    const wy = rows.weight[y]!;
    const above = pixelRows.low[y]! * analysisWidth;
    const below = pixelRows.high[y]! * analysisWidth;
    const vy = pixelRows.weight[y]!;
    for (let x = 0; x < target.width; x += 1) {
      const index = (y * target.width + x) * 4;
      const left = columns.low[x]!;
      const right = columns.high[x]!;
      const wx = columns.weight[x]!;
      const pixelLeft = pixelColumns.low[x]!;
      const pixelRight = pixelColumns.high[x]!;
      const vx = pixelColumns.weight[x]!;
      const share = (1 - vy) * ((1 - vx) * weights[above + pixelLeft]! + vx * weights[above + pixelRight]!) + vy * ((1 - vx) * weights[below + pixelLeft]! + vx * weights[below + pixelRight]!);
      pixelLab(source, index, lab);
      for (let c = 0; c < 3; c += 1) {
        const grid = grids[c]!;
        lab[c]! += share * ((1 - wy) * ((1 - wx) * grid[top + left]! + wx * grid[top + right]!) + wy * ((1 - wx) * grid[bottom + left]! + wx * grid[bottom + right]!));
      }
      labRgb(lab[0]!, lab[1]!, lab[2]!, rgba, index);
      rgba[index + 3] = source[index + 3]!;
    }
  }
  return rgba;
};

/** Alpha-weighted area average onto `width` x `height`, so transparent pixels lend no colour. */
export const downscale = (image: ImageData, width: number, height: number) => {
  const sums = new Float64Array(width * height * 4);
  const counts = new Uint32Array(width * height);
  const columns = Uint32Array.from({ length: image.width }, (_, x) => Math.floor(x * width / image.width));
  for (let y = 0; y < image.height; y += 1) {
    const row = Math.floor(y * height / image.height) * width;
    for (let x = 0; x < image.width; x += 1) {
      const from = (y * image.width + x) * 4;
      const to = row + columns[x]!;
      const alpha = image.data[from + 3]!;
      for (let c = 0; c < 3; c += 1) sums[to * 4 + c]! += image.data[from + c]! * alpha;
      sums[to * 4 + 3]! += alpha;
      counts[to]! += 1;
    }
  }
  const out = new ImageData(width, height);
  for (let i = 0; i < width * height; i += 1) {
    const alpha = sums[i * 4 + 3]!;
    for (let c = 0; c < 3; c += 1) out.data[i * 4 + c] = alpha > 0 ? sums[i * 4 + c]! / alpha : 0;
    out.data[i * 4 + 3] = alpha / counts[i]!;
  }
  return out;
};

export type DriftHarmonizationJob = {
  /** The visible layers under the layer, rendered with alpha over `underlyingWorldRect`. */
  underlying: ImageData;
  underlyingWorldRect: Rect;
  layerWorldRect: Rect;
  transform: StudioLayerTransform;
  /** Resolution the drift is measured and solved at; the layer is corrected at its own. */
  analysisWidth: number;
  analysisHeight: number;
};

/**
 * The Studio's standard Harmonize (the legacy one is Harmonize Upscale): DRIFT_HARMONIZATION_METHOD on `layer`, returned as a new image. Null is a
 * deterministic refusal (too little unchanged content to measure), decided before the full-resolution pass.
 */
export const harmonizeLayerDrift = async (cv: CV, layer: ImageData, job: DriftHarmonizationJob) => {
  const { analysisWidth, analysisHeight, layerWorldRect } = job;
  const analysisLayer = analysisWidth === layer.width && analysisHeight === layer.height ? layer : downscale(layer, analysisWidth, analysisHeight);
  const underlying = reprojectStudioLayerWorldImageToLocal(
    job.underlying, job.underlyingWorldRect, layerWorldRect, job.transform,
    { x: 0, y: 0, w: layerWorldRect.w, h: layerWorldRect.h }, analysisWidth, analysisHeight,
  );
  const field = await measureDrift(cv, underlying, analysisLayer, DRIFT_HARMONIZATION_METHOD);
  return field && new ImageData(applyDriftField(layer, field), layer.width, layer.height);
};
